import { Stack } from "@altimateai/lego";
import { Typography, Combobox } from "@altimateai/lego";
import { DateRange } from "@/components/dateRange";
import { InsightStatus } from "../constants";
import { InsightSortDropdown } from "./InsightSortDropdown";
import { setEndDate, setSelectedTab, setStartDate } from "../context/insightSlice";
import { InsightContext, useInsightState } from "../context/InsightContextProvider";
import { useContext } from "react";
import dayjs from "dayjs";

/* Header with title, description, date range, and sort */

interface InsightSectionHeaderProps {
  /**
   * When provided (by pages that own the date in the URL), the picker calls
   * this directly so the URL is the single source of truth. We still mirror
   * into context so the picker reflects the change before the URL round-trip
   * re-renders the tree.
   */
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
}

const InsightSectionHeader = ({ onDateRangeChange }: InsightSectionHeaderProps) => {
  const { selectedTab, startDate, endDate, description } = useInsightState();
  const { dispatch } = useContext(InsightContext);

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    dispatch(setStartDate(startDate));
    dispatch(setEndDate(endDate));
    onDateRangeChange?.(startDate, endDate);
  };

  const handleStatusChange = (value: string[]) => {
    dispatch(setSelectedTab(value as InsightStatus[]));
  };
  return (
    <div className="al-mb-4">
      <Stack direction="column" gap="xs">
        <Stack direction="row" align="center" justify="between">
          <Typography variant="section-heading">Opportunities</Typography>
          <Stack direction="row" align="center" gap="md">
            <DateRange
              startDate={dayjs(startDate).toDate()}
              endDate={dayjs(endDate).toDate()}
              availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
              disableCalendarSelection
              onDateRangeSelect={handleDateRangeChange}
              wrapperClass="[&>div[class*='datePicker']]:al-bg-white [&>div[class*='datePicker']]:al-border-input"
              label="Last detected:"
            />
            <Combobox
              options={Object.keys(InsightStatus).map(key => {
                const keyString = InsightStatus[key as keyof typeof InsightStatus];
                return {
                  label: keyString.charAt(0).toUpperCase() + keyString.slice(1),
                  value: keyString,
                };
              })}
              value={selectedTab}
              multiSelect
              onChange={handleStatusChange}
              placeholder="All opportunities"
              buttonProps={{ className: "al-max-w-[200px] al-min-w-32 !al-w-auto", size: "sm" }}
            />
            <InsightSortDropdown />
          </Stack>
        </Stack>
        {description && (
          <Typography size="sm" className="al-text-muted-foreground">
            {description}
          </Typography>
        )}
      </Stack>
    </div>
  );
};

export default InsightSectionHeader;
